import { useState, useEffect, useRef } from 'react';
import {
  ListDataValue,
  TypographyStyled,
  StyledAccordionSummary,
  RemoveItemIcon,
  RemoveItemButton,
  RemoveItemContainer,
  StyledAccordion,
  SectionDivider,
  StyledAccordionDetails
} from './general-panel.styles';
import { ReactComponent as RemoveItem } from '../../../svg/removeItem.svg';
import { ReactComponent as RemoveItemSelected } from '../../../svg/removeItemSelected.svg';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { HealthClaim } from '../../../types';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { singleProductActions, singleProductSelectors } from '../../../store/single-product';
import { productsSelectors } from '../../../store/products';
import { HealthClaimsComboBox } from './health-claims-combo-box';
import { ListTooltipStyle } from '../common/tooltip.styles';
import { StyledHealthClaimList, StyledHealthClaimListItem } from './health-claims-panel.styles';
import { PanelProps } from './panel-types';

export const HEALTH_CLAIMS_CAP: string = 'Health-claims';
export const HEALTH_CLAIMS_LOW: string = 'health-claims';

export function HealthClaimsPanel(props: PanelProps): JSX.Element {
  const currentHealthClaims = useSelector((state: RootState) =>
    singleProductSelectors.selectHealthClaims(state.singleProduct)
  );

  const healthClaims = useSelector((state: RootState) =>
    productsSelectors.selectHealthClaims(state.products)
  );

  const bottomRef = useRef<HTMLHRElement>(null);
  const [addedHealthClaim, setAddedHealthClaim] = useState<boolean>(false);

  const dispatch = useDispatch();

  const healthClaimsComboBox = createArrayHealthClaimsComboBox(healthClaims, currentHealthClaims);

  function dietsToArray() {
    let healthClaimsToDisplay = healthClaims.filter((healthClaim) =>
      currentHealthClaims.includes(healthClaim.key)
    );
    return healthClaimsToDisplay
      ? healthClaimsToDisplay.map((healthClaim) => healthClaim.displayName)
      : [];
  }

  const diets = dietsToArray();

  function createArrayHealthClaimsComboBox(
    healthClaims: HealthClaim[],
    currentHealthClaims: string[]
  ) {
    let HealthClaimsComboBoxArray: HealthClaim[] = [];
    HealthClaimsComboBoxArray = healthClaims.filter(
      (healthClaim) => !currentHealthClaims.includes(healthClaim.key)
    );
    return HealthClaimsComboBoxArray;
  }

  function handleDeleteHealthClaims(value: string) {
    props.setIsChange && props.setIsChange(true);
    let deletedHealthClaim = healthClaims.find((healthClaim) => healthClaim.displayName === value);
    let newhealthClaimsList = currentHealthClaims.filter(
      (healthClaim) => healthClaim !== deletedHealthClaim?.key
    );

    dispatch(
      singleProductActions.updateProduct({
        fieldName: 'healthClaims',
        value: newhealthClaimsList
      })
    );
  }

  function handleAddHealthClaim(value: string) {
    props.setIsChange && props.setIsChange(true);

    let newhealthClaim = healthClaimsComboBox.find(
      (healthClaim) => healthClaim.displayName === value
    );

    let newhealthClaimsList = [...currentHealthClaims];
    if (newhealthClaim?.key) newhealthClaimsList.push(newhealthClaim?.key);
    dispatch(
      singleProductActions.updateProduct({
        fieldName: 'healthClaims',
        value: newhealthClaimsList
      })
    );
    setAddedHealthClaim(true);
  }

  useEffect(() => {
    if (addedHealthClaim) {
      bottomRef?.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }
    setAddedHealthClaim(false);
    // eslint-disable-next-line
  }, [diets]);

  return (
    <div>
      <StyledAccordion
        id={`${HEALTH_CLAIMS_LOW}-accordion`}
        expanded={props.expanded === HEALTH_CLAIMS_CAP}
        onChange={props.handleAccordionChange(HEALTH_CLAIMS_CAP, diets.length)}
        disableGutters
        elevation={0}
        TransitionProps={{
          onEntered: (node: HTMLElement) => {
            bottomRef?.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
          }
        }}
      >
        <StyledAccordionSummary
          expandIcon={(props.allowEditing || diets.length > 0) && <ExpandMoreIcon id={`${HEALTH_CLAIMS_LOW}-expand-more-icon`}/>}
          aria-controls={`${HEALTH_CLAIMS_LOW}-content`}
          id={`${HEALTH_CLAIMS_LOW}-header`}
        >
          <TypographyStyled>
            {props.expanded === HEALTH_CLAIMS_CAP
              ? HEALTH_CLAIMS_CAP
              : `${HEALTH_CLAIMS_CAP} (${diets.length})`}
          </TypographyStyled>
        </StyledAccordionSummary>
        <StyledAccordionDetails>
          {props.allowEditing && healthClaimsComboBox && (
            <HealthClaimsComboBox
              values={healthClaimsComboBox}
              valueAddedHandler={handleAddHealthClaim}
            />
          )}
          {diets.length > 0 && (
            <StyledHealthClaimList id={`${HEALTH_CLAIMS_LOW}-list`} disablePadding dense>
              {diets.map((value, index) => (
                <StyledHealthClaimListItem key={value} index={index} className='textList'>
                  <ListTooltipStyle
                    id={`${index}-${value}-tooltip`}
                    value={value}
                    minCharacters={25}
                    child={<ListDataValue id={`${value}-tooltip`}>{value}</ListDataValue>}
                  />
                  {props.allowEditing && (
                    <RemoveItemContainer className='removeItem'>
                      <RemoveItemIcon id={`${value}-remove-icon`}>
                        <RemoveItem />
                      </RemoveItemIcon>
                      <RemoveItemButton
                        id={`${value}-remove-button`}
                        onClick={() => {
                          handleDeleteHealthClaims(value);
                        }}
                      >
                        <RemoveItemSelected />
                      </RemoveItemButton>
                    </RemoveItemContainer>
                  )}
                </StyledHealthClaimListItem>
              ))}
            </StyledHealthClaimList>
          )}
        </StyledAccordionDetails>
      </StyledAccordion>
      <SectionDivider id={`${HEALTH_CLAIMS_LOW}-divider`} ref={bottomRef} />
    </div>
  );
}
